import {
  createMetamodelRegistry,
  renderMetamodelGraphqlSnapshotSource,
} from "@granular-software/metamodel-core";
import { effectBehaviorsMetamodelPackage } from "@granular-software/metamodel-effect-behaviors";
import { enumMetamodelPackage } from "@granular-software/metamodel-enum";
import { filterByMetamodelPackage } from "@granular-software/metamodel-filter-by";
import { noteMetamodelPackage } from "@granular-software/metamodel-note";
import { policyMetamodelPackage } from "@granular-software/metamodel-policy";
import { requiredMetamodelPackage } from "@granular-software/metamodel-required";
import { searchableMetamodelPackage } from "@granular-software/metamodel-searchable";
import { stateMachineMetamodelPackage } from "@granular-software/metamodel-state-machine";
import { temporalMetamodelPackage } from "@granular-software/metamodel-temporal";
import { validationRuleMetamodelPackage } from "@granular-software/metamodel-validation-rule";

export const DEFAULT_METAMODEL_PACKAGES = [
  noteMetamodelPackage,
  requiredMetamodelPackage,
  enumMetamodelPackage,
  filterByMetamodelPackage,
  searchableMetamodelPackage,
  validationRuleMetamodelPackage,
  stateMachineMetamodelPackage,
  temporalMetamodelPackage,
  effectBehaviorsMetamodelPackage,
  policyMetamodelPackage,
];

export const DEFAULT_METAMODEL_REGISTRY = createMetamodelRegistry(
  DEFAULT_METAMODEL_PACKAGES,
);

export function getDefaultMetamodelPackages() {
  return DEFAULT_METAMODEL_REGISTRY.packages;
}

export function collectMetamodelDocRows(
  kind: "fieldRows" | "modelRows" | "effectRows",
) {
  return DEFAULT_METAMODEL_REGISTRY.collectDocRows(kind);
}

export function getDefaultMetamodelSummarySelections() {
  return DEFAULT_METAMODEL_REGISTRY.getSummarySelections();
}

export function applyDefaultMetamodelSummaryReadersToClass(
  rawClass: Record<string, any>,
  classSummary: Parameters<
    typeof DEFAULT_METAMODEL_REGISTRY.applyClassSummaryReaders
  >[1],
) {
  return DEFAULT_METAMODEL_REGISTRY.applyClassSummaryReaders(
    rawClass,
    classSummary,
  );
}

export function applyDefaultMetamodelSummaryReadersToProperty(
  rawProperty: Record<string, any>,
  propertySummary: Parameters<
    typeof DEFAULT_METAMODEL_REGISTRY.applyPropertySummaryReaders
  >[1],
) {
  return DEFAULT_METAMODEL_REGISTRY.applyPropertySummaryReaders(
    rawProperty,
    propertySummary,
  );
}

export function applyDefaultMetamodelSummaryReadersToMethod(
  rawMethod: Record<string, any>,
  methodSummary: Parameters<
    typeof DEFAULT_METAMODEL_REGISTRY.applyMethodSummaryReaders
  >[1],
) {
  return DEFAULT_METAMODEL_REGISTRY.applyMethodSummaryReaders(
    rawMethod,
    methodSummary,
  );
}

export function applyDefaultMetamodelPropertyIR(
  propertyIR: Parameters<typeof DEFAULT_METAMODEL_REGISTRY.applyPropertyIR>[0],
  propertySummary: Parameters<
    typeof DEFAULT_METAMODEL_REGISTRY.applyPropertyIR
  >[1],
  classSummary: Parameters<
    typeof DEFAULT_METAMODEL_REGISTRY.applyPropertyIR
  >[2],
) {
  return DEFAULT_METAMODEL_REGISTRY.applyPropertyIR(
    propertyIR,
    propertySummary,
    classSummary,
  );
}

export function applyDefaultMetamodelClassIR(
  classIR: Parameters<typeof DEFAULT_METAMODEL_REGISTRY.applyClassIR>[0],
  classSummary: Parameters<typeof DEFAULT_METAMODEL_REGISTRY.applyClassIR>[1],
) {
  return DEFAULT_METAMODEL_REGISTRY.applyClassIR(classIR, classSummary);
}

export function applyDefaultMetamodelMethodIR(
  methodIR: Parameters<typeof DEFAULT_METAMODEL_REGISTRY.applyMethodIR>[0],
  methodSummary: Parameters<typeof DEFAULT_METAMODEL_REGISTRY.applyMethodIR>[1],
  classSummary?: Parameters<typeof DEFAULT_METAMODEL_REGISTRY.applyMethodIR>[2],
) {
  return DEFAULT_METAMODEL_REGISTRY.applyMethodIR(
    methodIR,
    methodSummary,
    classSummary,
  );
}

export function getDefaultMetamodelExtraTypes(
  classSummary: Parameters<
    typeof DEFAULT_METAMODEL_REGISTRY.getExtraTypesForClass
  >[0],
) {
  return DEFAULT_METAMODEL_REGISTRY.getExtraTypesForClass(classSummary);
}

export function getDefaultMetamodelExtraMethods(
  classSummary: Parameters<
    typeof DEFAULT_METAMODEL_REGISTRY.getExtraMethodsForClass
  >[0],
) {
  return DEFAULT_METAMODEL_REGISTRY.getExtraMethodsForClass(classSummary);
}

export function renderDefaultMetamodelGraphqlSnapshotSource() {
  return renderMetamodelGraphqlSnapshotSource(
    DEFAULT_METAMODEL_REGISTRY.packages,
  );
}
